/**
 * Utilities for preparing chart data for export (DOCX, clipboard copy).
 *
 * The core problem: LLM-generated chart configs often contain bare JavaScript
 * functions (formatters, tick callbacks, etc.) which are not valid JSON.
 * `JSON.stringify()` silently drops function values and `JSON.parse()` rejects
 * them outright.
 *
 * For *rendering* in the browser we eval() them into real functions
 * (see processChartData in ChartViewer.tsx). But for *export* we need the
 * opposite — functions must stay as serializable strings because the chart
 * renderer server.js will revive them on the other end. We wrap them as
 * `"##function(...){...}##"` strings, which is the format server.js's
 * `reviveFunctions()` already understands.
 */

/**
 * Recursively strips empty objects ({}) from a chart config.
 * LLMs sometimes generate empty objects for callback fields which crash Chart.js.
 */
function stripEmptyObjects(obj: any): any {
  if (Array.isArray(obj)) return obj.map(stripEmptyObjects);
  if (obj && typeof obj === "object") {
    for (const key of Object.keys(obj)) {
      obj[key] = stripEmptyObjects(obj[key]);
      if (
        obj[key] &&
        typeof obj[key] === "object" &&
        !Array.isArray(obj[key]) &&
        Object.keys(obj[key]).length === 0
      ) {
        delete obj[key];
      }
    }
  }
  return obj;
}

/**
 * Scans a raw chart config string (which may contain bare JS functions) and
 * wraps each function body in `"##...##"` so the result is valid JSON.
 *
 * Handles:
 *   - `function(args) { ... }`  (traditional functions)
 *   - `(args) => { ... }`       (arrow functions with block body)
 *   - `(args) => expr`          (arrow functions with expression body)
 */
function stringifyFunctions(text: string): string {
  const result: string[] = [];
  let i = 0;

  while (i < text.length) {
    // Look for `function` keyword not preceded by an alphanumeric (i.e. not
    // part of a longer identifier like "myfunction").
    const funcIdx = text.indexOf("function", i);
    // Look for arrow functions: `(...)  =>`
    const arrowIdx = findArrowFunction(text, i);

    // Pick whichever comes first
    let matchIdx = -1;
    let isArrow = false;
    if (funcIdx === -1 && arrowIdx === -1) {
      // No more functions — emit the rest
      result.push(text.slice(i));
      break;
    } else if (funcIdx === -1) {
      matchIdx = arrowIdx;
      isArrow = true;
    } else if (arrowIdx === -1) {
      matchIdx = funcIdx;
    } else if (arrowIdx < funcIdx) {
      matchIdx = arrowIdx;
      isArrow = true;
    } else {
      matchIdx = funcIdx;
    }

    // Verify `function` is not part of a larger word
    if (!isArrow && matchIdx > 0) {
      const prev = text[matchIdx - 1];
      if (/[a-zA-Z0-9_$]/.test(prev)) {
        // Part of a longer identifier — skip past it
        result.push(text.slice(i, matchIdx + 8));
        i = matchIdx + 8;
        continue;
      }
    }

    // Emit everything before the function
    result.push(text.slice(i, matchIdx));

    // Extract the full function text
    const funcText = isArrow
      ? extractArrowFunction(text, matchIdx)
      : extractTraditionalFunction(text, matchIdx);

    if (funcText === null) {
      // Could not extract — emit as-is and move past
      result.push(text[matchIdx]);
      i = matchIdx + 1;
      continue;
    }

    // Wrap in "##...##"
    const escaped = funcText
      .replace(/\\/g, "\\\\")
      .replace(/"/g, '\\"')
      .replace(/\n/g, "\\n")
      .replace(/\r/g, "\\r")
      .replace(/\t/g, "\\t");
    result.push(`"##${escaped}##"`);
    i = matchIdx + funcText.length;
  }

  return result.join("");
}

/** Find the start index of an arrow function `(...)  =>` starting from `from`. */
function findArrowFunction(text: string, from: number): number {
  let idx = from;
  while (idx < text.length) {
    const parenIdx = text.indexOf("(", idx);
    if (parenIdx === -1) return -1;

    // Check context — if inside a string literal, skip
    // Simple heuristic: the char before `(` shouldn't be a quote
    // (This won't catch all cases, but handles the common ones)

    // Find matching `)`
    const closeIdx = findMatchingParen(text, parenIdx);
    if (closeIdx === -1) {
      idx = parenIdx + 1;
      continue;
    }

    // Look for `=>` after `)`, skipping whitespace
    let j = closeIdx + 1;
    while (j < text.length && /\s/.test(text[j])) j++;
    if (j + 1 < text.length && text[j] === "=" && text[j + 1] === ">") {
      // Verify this is in a value position (preceded by `:` or `,` after
      // skipping whitespace, or at start). Walk backwards from parenIdx.
      let k = parenIdx - 1;
      while (k >= 0 && /\s/.test(text[k])) k--;
      if (k >= 0 && (text[k] === ":" || text[k] === "," || text[k] === "[")) {
        return parenIdx;
      }
    }
    idx = parenIdx + 1;
  }
  return -1;
}

function findMatchingParen(text: string, openIdx: number): number {
  let depth = 1;
  let i = openIdx + 1;
  while (i < text.length && depth > 0) {
    if (text[i] === "(") depth++;
    else if (text[i] === ")") depth--;
    i++;
  }
  return depth === 0 ? i - 1 : -1;
}

function findMatchingBrace(text: string, openIdx: number): number {
  let depth = 1;
  let i = openIdx + 1;
  let inString: string | null = null;
  while (i < text.length && depth > 0) {
    const ch = text[i];
    if (inString) {
      if (ch === "\\" && i + 1 < text.length) {
        i += 2;
        continue;
      }
      if (ch === inString) inString = null;
    } else {
      if (ch === '"' || ch === "'" || ch === "`") inString = ch;
      else if (ch === "{") depth++;
      else if (ch === "}") depth--;
    }
    i++;
  }
  return depth === 0 ? i - 1 : -1;
}

/** Extract `function(args) { body }` starting at `start`. */
function extractTraditionalFunction(
  text: string,
  start: number
): string | null {
  // Skip `function`
  let i = start + 8;
  // Optional function name
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i < text.length && /[a-zA-Z_$]/.test(text[i])) {
    while (i < text.length && /[a-zA-Z0-9_$]/.test(text[i])) i++;
  }
  // Skip whitespace to `(`
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i >= text.length || text[i] !== "(") return null;
  const closeParenIdx = findMatchingParen(text, i);
  if (closeParenIdx === -1) return null;

  // Skip whitespace to `{`
  i = closeParenIdx + 1;
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i >= text.length || text[i] !== "{") return null;
  const closeBraceIdx = findMatchingBrace(text, i);
  if (closeBraceIdx === -1) return null;

  return text.slice(start, closeBraceIdx + 1);
}

/** Extract arrow function starting at `(` (the start of params). */
function extractArrowFunction(text: string, start: number): string | null {
  const closeParenIdx = findMatchingParen(text, start);
  if (closeParenIdx === -1) return null;

  // Find `=>`
  let i = closeParenIdx + 1;
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i + 1 >= text.length || text[i] !== "=" || text[i + 1] !== ">")
    return null;
  i += 2; // past `=>`

  // Skip whitespace
  while (i < text.length && /\s/.test(text[i])) i++;

  if (i >= text.length) return null;

  if (text[i] === "{") {
    // Block body
    const closeBraceIdx = findMatchingBrace(text, i);
    if (closeBraceIdx === -1) return null;
    return text.slice(start, closeBraceIdx + 1);
  } else {
    // Expression body — read until we hit `,` or `}` at depth 0
    // (i.e., end of this JSON value)
    const exprStart = i;
    let depth = 0;
    while (i < text.length) {
      const ch = text[i];
      if (ch === "(" || ch === "[") depth++;
      else if (ch === ")" || ch === "]") depth--;
      else if (depth === 0 && (ch === "," || ch === "}" || ch === "]")) break;
      i++;
    }
    // Trim trailing whitespace from expression
    let end = i;
    while (end > exprStart && /\s/.test(text[end - 1])) end--;
    if (end <= exprStart) return null;
    return text.slice(start, end);
  }
}

/**
 * Strip ##...## delimiters from function strings and nullify arrow functions.
 * Matches the PDF export's `strip_arrow_functions()` in reports.py.
 *
 * - `"##function(x){...}##"` → `"function(x){...}"` (server.js can revive)
 * - `"##(x) => ...##"` or `"(x) => ..."` → `null` (server.js uses defaults)
 */
function cleanFunctionStrings(obj: any): any {
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      if (typeof obj[i] === "string") {
        let s = obj[i].trim();
        if (s.startsWith("##") && s.endsWith("##")) {
          s = s.slice(2, -2).trim();
        }
        if (/^\(.*\)\s*=>/.test(s)) {
          obj[i] = null;
        } else if (s !== obj[i].trim()) {
          obj[i] = s;
        } else {
          cleanFunctionStrings(obj[i]);
        }
      } else {
        cleanFunctionStrings(obj[i]);
      }
    }
  } else if (obj && typeof obj === "object") {
    for (const key of Object.keys(obj)) {
      if (typeof obj[key] === "string") {
        let s = obj[key].trim();
        if (s.startsWith("##") && s.endsWith("##")) {
          s = s.slice(2, -2).trim();
        }
        if (/^\(.*\)\s*=>/.test(s)) {
          obj[key] = null;
        } else if (s !== obj[key].trim()) {
          obj[key] = s;
        } else {
          cleanFunctionStrings(obj[key]);
        }
      } else {
        cleanFunctionStrings(obj[key]);
      }
    }
  }
  return obj;
}

/**
 * Full pipeline: converts raw chart data (from LLM / Redux) into a JSON-safe
 * object suitable for sending to the chart renderer API.
 *
 * JS functions are preserved as plain string values (e.g.
 * `"function(v) { return v; }"`) which server.js's `reviveFunctions()`
 * converts back to real functions.  Arrow functions are nullified since
 * server.js cannot reliably revive them (matches PDF export behavior).
 *
 * Returns `null` if the data cannot be parsed.
 */
export function parseChartForExport(rawData: any): any | null {
  let data = rawData;

  // 1. Unwrap { chart, metadata } wrapper if present
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    "chart" in data
  ) {
    data = data.chart;
    if (!data) return null;
  }

  // 2. If already an object, deep-clone while preserving functions as strings
  if (typeof data === "object" && data !== null) {
    try {
      const serialized = JSON.stringify(data, (_key, value) => {
        if (typeof value === "function") {
          return value.toString();
        }
        return value;
      });
      const cloned = JSON.parse(serialized);
      stripEmptyObjects(cloned);
      cleanFunctionStrings(cloned);
      return cloned;
    } catch {
      // Fall through to string handling
    }
  }

  // 3. Handle string data
  if (typeof data !== "string") return null;

  let text = data.trim();

  // Strip markdown code fences
  if (text.startsWith("```json") && text.endsWith("```")) {
    text = text.slice(7, -3).trim();
  } else if (text.startsWith("```") && text.endsWith("```")) {
    text = text.slice(3, -3).trim();
  }

  // 4. Try JSON.parse first (fast path for valid JSON)
  try {
    const parsed = JSON.parse(text);
    stripEmptyObjects(parsed);
    cleanFunctionStrings(parsed);
    return parsed;
  } catch {
    // Not valid JSON — likely contains bare JS functions
  }

  // 5. Wrap JS functions as quoted strings, then parse
  try {
    const jsonified = stringifyFunctions(text);
    const parsed = JSON.parse(jsonified);
    stripEmptyObjects(parsed);
    cleanFunctionStrings(parsed);
    return parsed;
  } catch {
    return null;
  }
}
